import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { isNativeApp } from './native';

// Push-Nachrichten im Browser: iPhone, wenn die App vom Home-Bildschirm läuft (ab iOS 16.4), und Browser am PC. Die
// Android-App hat ihre eigenen (native.ts). Einschalten geht nur auf Tippen (iPhone verlangt das), danach meldet sich
// der Browser bei jedem Login von selbst wieder an.
//   off:    geht hier nicht (Safari ohne Home-Bildschirm, Android-App, alter Browser, Server ohne Push)
//   ask:    noch nicht gefragt, Knopf „Einschalten“ zeigen
//   on:     eingeschaltet
//   denied: abgelehnt (nur in den Einstellungen des Browsers wieder einzuschalten)
export type WebPushState = 'off' | 'ask' | 'on' | 'denied';

let state: WebPushState = 'off';
let ready: { reg: ServiceWorkerRegistration; key: string } | null = null;
const listeners = new Set<() => void>();
function set(next: WebPushState) {
  state = next;
  for (const l of listeners) l();
}

function supported() {
  return !isNativeApp() && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

function keyBytes(key: string): Uint8Array<ArrayBuffer> {
  const b64 = key.replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4)), (c) => c.charCodeAt(0));
}

async function register(sub: PushSubscription) {
  const { endpoint, keys } = sub.toJSON();
  await api.put('/api/push/device', { platform: 'web', token: endpoint, keys });
}

// Hinweis „Push-Nachrichten einschalten“ auf der Übersicht in diesem Browser ausblenden (Nicht jetzt, oder der Browser
// kann es nicht). Einschalten geht danach weiter über das Konto-Menü.
const HIDE_KEY = 'push-hinweis-aus';
let hidden = (() => {
  try {
    return localStorage.getItem(HIDE_KEY) === '1';
  } catch {
    return false;
  }
})();
export function hideWebPushNotice() {
  hidden = true;
  try {
    localStorage.setItem(HIDE_KEY, '1');
  } catch {
    // ohne Speicher: nur bis zum Neuladen ausgeblendet
  }
  for (const l of listeners) l();
}

// Auf Tippen: fragt nach der Erlaubnis (Browser zeigt seine Abfrage) und meldet den Browser an.
//   on: eingeschaltet; denied: im Browser blockiert; failed: Browser kann es nicht (z. B. Abfrage still unterdrückt)
// Sam 13.10.: Am PC tat der Knopf scheinbar nichts und der Hinweis blieb stehen. Jetzt gibt es immer eine Antwort, und
// manche Browser (Safari am Mac, Chrome mit leiser Abfrage) bekommen die Erlaubnis vorher über Notification.
export async function enableWebPush(): Promise<'on' | 'denied' | 'failed'> {
  if (!ready) return 'failed';
  const { reg, key } = ready;
  const opts = { userVisibleOnly: true, applicationServerKey: keyBytes(key) };
  try {
    let sub: PushSubscription;
    try {
      // ohne vorheriges await: Das iPhone fragt nur, wenn das direkt aufs Tippen folgt
      sub = await reg.pushManager.subscribe(opts);
    } catch (err) {
      if (Notification.permission !== 'default' || (await Notification.requestPermission()) !== 'granted') throw err;
      sub = await reg.pushManager.subscribe(opts);
    }
    await register(sub);
    set('on');
    return 'on';
  } catch (err) {
    console.warn('Push-Nachrichten einschalten ging nicht', err);
    if (Notification.permission === 'denied') {
      set('denied');
      return 'denied';
    }
    hideWebPushNotice();
    return 'failed';
  }
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};

export function useWebPushState(): WebPushState {
  return useSyncExternalStore(subscribe, () => state);
}

// Hinweis auf der Übersicht ausgeblendet (Nicht jetzt)
export function useWebPushNoticeHidden(): boolean {
  return useSyncExternalStore(subscribe, () => hidden);
}

// Einmal je Login (in App): Service Worker anmelden, Stand ermitteln, bei Erlaubnis den Browser wieder anmelden (die
// Anmeldung hängt an der Sitzung). Antippen einer Nachricht öffnet die Seite in der laufenden App.
export function useWebPush(userId: string | null) {
  const navigate = useNavigate();
  const go = useRef(navigate);
  go.current = navigate;
  useEffect(() => {
    if (!userId || !supported()) return;
    let gone = false;
    const onMessage = (e: MessageEvent) => {
      const url = (e.data as { type?: string; url?: unknown } | null)?.url;
      if ((e.data as { type?: string } | null)?.type === 'push-open' && typeof url === 'string' && url.startsWith('/')) {
        go.current(url);
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    (async () => {
      const { webKey } = await api.get<{ webKey: string | null }>('/api/push');
      if (!webKey || gone) return;
      const reg = await navigator.serviceWorker.register('/sw.js');
      if (gone) return;
      ready = { reg, key: webKey };
      if (Notification.permission === 'denied') return set('denied');
      const sub = Notification.permission === 'granted' ? await reg.pushManager.getSubscription() : null;
      if (!sub) return set('ask');
      // Schlüssel des Servers neu (z. B. Datenbank zurückgespielt): alte Anmeldung gilt nicht mehr
      const same = sub.options.applicationServerKey
        ? keyBytes(webKey).join() === new Uint8Array(sub.options.applicationServerKey).join()
        : true;
      if (!same) {
        await sub.unsubscribe();
        return set('ask');
      }
      await register(sub);
      if (!gone) set('on');
    })().catch(() => {});
    return () => {
      gone = true;
      ready = null;
      set('off');
      navigator.serviceWorker.removeEventListener('message', onMessage);
    };
  }, [userId]);
}
